// v19.44 — Admin analytics rebuilt: Beat Store section first, global
// window selector, every card labeled with its window.
// Route: /admin
import GlobalNav from "../components/GlobalNav";
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { SignOut, MusicNote, DownloadSimple, Users, Storefront, Coins, ChartLineUp, Calendar } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const usd = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const pct = (v) => `${(v ?? 0).toFixed(1)}%`;
const tierLabel = { mp3: "MP3 Lease", wav: "WAV + Stems Lease", stems: "Stems (legacy)", exclusive: "Exclusive Rights" };

const WINDOW_OPTIONS = [
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "60d", label: "60 days" },
  { id: "90d", label: "90 days" },
  { id: "all", label: "All time" },
];

export default function DashboardPage() {
  const [token, setToken] = useState(() => localStorage.getItem("crow_admin_token") || "");
  const [password, setPassword] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [win, setWin] = useState(() => localStorage.getItem("crow_admin_window") || "30d");

  const login = async () => {
    setLoading(true); setErr("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      setToken(res.data.token);
      localStorage.setItem("crow_admin_token", res.data.token);
    } catch {
      setErr("Invalid password");
    } finally { setLoading(false); }
  };

  const fetchStats = useCallback(async (t = token, w = win) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/store/admin/analytics/overview?window=${w}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setStats(res.data);
    } catch (e) {
      setToken("");
      localStorage.removeItem("crow_admin_token");
      toast.error("Session expired");
    } finally { setLoading(false); }
  }, [token, win]);

  const logout = () => { setToken(""); setStats(null); localStorage.removeItem("crow_admin_token"); };

  useEffect(() => { if (token) fetchStats(token, win); }, [token, win, fetchStats]);

  const onWindowChange = (id) => {
    setWin(id);
    localStorage.setItem("crow_admin_window", id);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-3 rounded-2xl bg-[#0a0a0a] border border-[#1f1f1f] p-6">
          <h1 className="font-display font-bold text-2xl">Admin Dashboard</h1>
          <input
            type="password"
            className="w-full px-3 py-2 rounded bg-black border border-[#262626]"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            placeholder="Admin password"
            data-testid="admin-password-input"
          />
          {err ? <div className="text-red-400 text-sm">{err}</div> : null}
          <button
            onClick={login}
            className="w-full py-2 rounded font-bold bg-emerald-500 text-black disabled:opacity-50"
            disabled={loading}
            data-testid="admin-login-btn"
          >{loading ? "Signing in…" : "Sign in"}</button>
        </div>
      </div>
    );
  }

  if (!stats) return <div className="min-h-screen bg-black text-white p-8">Loading…</div>;

  const winLabel = stats.window_label || "last 30 days";
  const store = stats.store_funnel || {};
  const gen = stats.generator_funnel || {};
  const m = stats.money || {};

  return (
    <div className="min-h-screen bg-black text-white p-4 sm:p-6" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-3">
          <GlobalNav />
          <h1 className="font-display font-bold text-3xl mr-auto">Admin Analytics</h1>
          <Link to="/admin/beats" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-emerald-400/40 hover:text-emerald-300">Beat Store admin →</Link>
          <button onClick={logout} className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-red-400/40 hover:text-red-300 flex items-center gap-1.5" data-testid="admin-logout">
            <SignOut size={14} weight="bold" /> Log out
          </button>
        </div>

        {/* Global time window selector */}
        <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-white/5" data-testid="window-selector">
          <span className="text-xs text-white/50 flex items-center gap-1.5 pr-2">
            <Calendar size={14} weight="bold" /> Time window
          </span>
          {WINDOW_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => onWindowChange(opt.id)}
              className={`px-3 py-1 rounded-full text-xs font-bold border transition ${win === opt.id ? "bg-emerald-500 text-black border-emerald-500" : "bg-transparent text-white/70 border-white/10 hover:border-white/30"}`}
              data-testid={`window-${opt.id}`}
            >{opt.label}</button>
          ))}
          <span className="ml-auto text-xs text-white/40">Applies to every card below</span>
        </div>

        {/* ============ BEAT STORE (top of dashboard) ============ */}
        <SectionHeader icon={<Storefront size={18} weight="bold" className="text-emerald-400" />} title="Beat Store" subtitle="Revenue driver — real Stripe sales" />

        <Card title="Store conversion funnel" window={winLabel} testid="store-funnel-card">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <FunnelCell label="Beat page views" value={store.beat_page_views} testid="store-funnel-views" />
            <FunnelCell label="Checkout started" value={store.checkout_started} sub={`${pct(store.checkout_start_pct)} of views`} testid="store-funnel-started" />
            <FunnelCell label="Paid" value={store.paid} sub={`${pct(store.paid_pct_of_started)} of started · ${pct(store.paid_pct_of_views)} of views`} highlight testid="store-funnel-paid" />
          </div>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card title="Beat revenue" window={winLabel} testid="beat-revenue-card">
            <MoneyStat label={winLabel} value={usd(m.beat_revenue_cents)} testid="beat-rev-window" />
            <MoneyStat label="All time" value={usd(m.beat_revenue_all_cents)} testid="beat-rev-all" />
            <div className="grid grid-cols-2 gap-2 pt-2 mt-2 border-t border-white/5">
              <StatMini label={`Beat sales · ${winLabel}`} value={m.beat_sales_count} testid="beat-sales-window" />
              <StatMini label="Beat sales · all time" value={m.beat_sales_all_count} testid="beat-sales-all" />
            </div>
          </Card>

          <Card title="Sales by license tier" window={winLabel} testid="tier-card">
            <div className="grid grid-cols-2 gap-3">
              {["mp3", "wav", "stems", "exclusive"].map((t) => {
                const row = stats.beat_sales_by_tier?.[t] || { count: 0, revenue_cents: 0 };
                return (
                  <div key={t} className="rounded-lg bg-[#0a0a0a] border border-[#1f1f1f] p-3" data-testid={`tier-${t}`}>
                    <div className="text-[10px] uppercase tracking-wider text-white/50">{tierLabel[t]}</div>
                    <div className="text-lg font-black mt-1">{row.count}</div>
                    <div className="text-xs text-emerald-300 font-bold">{usd(row.revenue_cents)}</div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        <Card title="Top 10 beats by revenue" window={winLabel} testid="top-beats-card">
          {stats.top_beats?.length === 0 ? (
            <div className="text-white/40 text-sm">No sales in this window.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/50 text-xs uppercase tracking-wider">
                  <th className="pb-2">Beat</th>
                  <th className="pb-2">Producer</th>
                  <th className="pb-2 text-right">Sales</th>
                  <th className="pb-2 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {stats.top_beats.map((b) => (
                  <tr key={b.beat_id} className="border-t border-white/5" data-testid={`top-beat-${b.beat_id}`}>
                    <td className="py-2 font-bold">{b.beat_name}</td>
                    <td className="py-2 text-white/60">{b.artist_tag}</td>
                    <td className="py-2 text-right">{b.sales}</td>
                    <td className="py-2 text-right text-emerald-300 font-bold">{usd(b.revenue_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="UTM attribution — beat sales by source" window={winLabel} testid="utm-card">
          {stats.utm_sales?.length === 0 ? (
            <div className="text-white/40 text-sm">No attributed sales in this window.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/50 text-xs uppercase tracking-wider">
                  <th className="pb-2">Source</th>
                  <th className="pb-2">Campaign / Video</th>
                  <th className="pb-2 text-right">Sales</th>
                  <th className="pb-2 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {stats.utm_sales.map((s, i) => (
                  <tr key={`${s.source}-${s.campaign || i}`} className="border-t border-white/5" data-testid={`utm-row-${i}`}>
                    <td className="py-2 font-bold">{s.source}</td>
                    <td className="py-2 text-white/60">{s.campaign || "—"}</td>
                    <td className="py-2 text-right">{s.sales}</td>
                    <td className="py-2 text-right text-emerald-300 font-bold">{usd(s.revenue_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Recent beat purchases" window={winLabel} testid="recent-purchases-card">
          {stats.recent_purchases?.length === 0 ? (
            <div className="text-white/40 text-sm">No purchases in this window.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-white/50 text-xs uppercase tracking-wider">
                    <th className="pb-2">When</th>
                    <th className="pb-2">Beat</th>
                    <th className="pb-2">Tier</th>
                    <th className="pb-2">Buyer</th>
                    <th className="pb-2">Source</th>
                    <th className="pb-2 text-right">Amount</th>
                    <th className="pb-2 text-right">Fulfillment</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_purchases.map((p) => (
                    <tr key={p.id} className="border-t border-white/5" data-testid={`purchase-${p.id}`}>
                      <td className="py-2 text-white/50 text-xs">{(p.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="py-2 font-bold">{p.beat_name}</td>
                      <td className="py-2 text-xs uppercase tracking-wide text-white/60">{p.tier}</td>
                      <td className="py-2 text-white/70">{p.buyer_email}</td>
                      <td className="py-2 text-white/50">{p.utm_source}</td>
                      <td className="py-2 text-right text-emerald-300 font-bold">{usd(p.price_cents)}</td>
                      <td className={`py-2 text-right text-xs ${p.fulfillment_status === "complete" ? "text-emerald-300" : p.fulfillment_status === "partial" ? "text-yellow-300" : "text-white/40"}`}>{p.fulfillment_status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* ============ GENERATOR (below store) ============ */}
        <SectionHeader icon={<ChartLineUp size={18} weight="bold" className="text-sky-400" />} title="Beat Generator" subtitle="Free funnel — signups, activation, paywall" />

        <Card title="Generator funnel" window={winLabel} testid="generator-funnel-card">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <FunnelCell label="Visitors" value={gen.visitors} sub={gen.visitors > gen.visitors_tracked ? `(clamped from ${gen.visitors_tracked} tracked)` : ""} testid="gen-visitors" />
            <FunnelCell label="Signups" value={gen.signups} sub={`${pct(gen.signup_conversion_pct)} conv`} testid="gen-signups" />
            <FunnelCell label="Activated" value={gen.activated} sub={`${pct(gen.activation_pct)} of signups`} testid="gen-activated" />
            <FunnelCell label="Paywall hits" value={gen.paywall_hits} sub={`${pct(gen.paywall_pct)} of activated`} testid="gen-paywall" />
            <FunnelCell label="Paid" value={gen.paid} sub={`${pct(gen.trial_to_paid_pct)} trial→paid · ${pct(gen.visitor_to_paid_pct)} v→paid`} highlight testid="gen-paid" />
          </div>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card title="Total revenue" window={winLabel} testid="total-rev-card" compact>
            <MoneyStat label={winLabel} value={usd(m.total_revenue_cents)} big testid="total-rev-window" />
            <MoneyStat label="All time" value={usd(m.total_revenue_all_cents)} testid="total-rev-all" />
          </Card>
          <Card title="Credit packs" window={winLabel} testid="creditpack-card" compact>
            <MoneyStat label={winLabel} value={usd(m.credit_pack_revenue_cents)} testid="cp-rev-window" />
            <MoneyStat label="All time" value={usd(m.credit_pack_revenue_all_cents)} testid="cp-rev-all" />
          </Card>
          <Card title="Customer economics" window="all time" testid="econ-card" compact>
            <StatMini label="Paying customers" value={m.paying_customers_all} testid="paying-customers" />
            <StatMini label="Repeat purchase" value={pct(m.repeat_purchase_pct)} testid="repeat-pct" />
            <StatMini label={`Revenue / visitor · ${winLabel}`} value={`$${(m.revenue_per_visitor_usd ?? 0).toFixed(2)}`} testid="rev-per-visitor" />
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card title="Signups" window="all time" testid="signups-card" compact>
            <MoneyStat label={winLabel} value={gen.signups} testid="signups-window" />
            <MoneyStat label="All time" value={stats.signups_all || 0} testid="signups-all" />
          </Card>
          <Card title="Visitors" window="all time" testid="visitors-card" compact>
            <MoneyStat label={winLabel} value={gen.visitors} testid="visitors-window" />
            <MoneyStat label="All time" value={stats.visitors_all || 0} testid="visitors-all" />
          </Card>
          <Card title="Exports" window="all time" testid="exports-card" compact>
            <MoneyStat label={winLabel} value={stats.exports_windowed || 0} testid="exports-window" />
            <MoneyStat label="All time" value={stats.exports_total || 0} testid="exports-all" />
          </Card>
        </div>

        <CreditSalesCard sales={stats.recent_credit_sales || []} winLabel={winLabel} token={token} onRefresh={() => fetchStats(token, win)} />

        <Card title="Recently joined" window={winLabel} testid="recent-users-card">
          {stats.recent_users?.length === 0 ? (
            <div className="text-white/40 text-sm">No new signups in this window.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-white/50 text-xs uppercase tracking-wider">
                    <th className="pb-2">Joined</th>
                    <th className="pb-2">Email</th>
                    <th className="pb-2">Username</th>
                    <th className="pb-2 text-right">Beats gen'd</th>
                    <th className="pb-2 text-right">Credits</th>
                    <th className="pb-2 text-right">Paid?</th>
                    <th className="pb-2">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_users.map((u) => (
                    <tr key={u.id} className="border-t border-white/5" data-testid={`user-${u.id}`}>
                      <td className="py-2 text-white/50 text-xs">{(u.created_at || "").slice(0, 10)}</td>
                      <td className="py-2 text-white/80">{u.email}</td>
                      <td className="py-2 text-white/60">{u.username}</td>
                      <td className="py-2 text-right">{u.beats_generated}</td>
                      <td className="py-2 text-right text-white/60">{u.credits_remaining}</td>
                      <td className={`py-2 text-right ${u.purchased ? "text-emerald-300" : "text-white/30"}`}>{u.purchased ? "yes" : "no"}</td>
                      <td className="py-2 text-white/50">{u.utm_source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="text-[10px] text-white/30 pt-4 border-t border-white/5">
          Generated {(stats.generated_at || "").slice(0, 19).replace("T", " ")} UTC · window: {winLabel}
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div className="flex items-baseline gap-3 pt-2">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="font-display font-bold text-xl">{title}</h2>
      </div>
      {subtitle ? <span className="text-xs text-white/40">{subtitle}</span> : null}
    </div>
  );
}

function Card({ title, window, children, compact = false, testid }) {
  return (
    <div className={`rounded-2xl bg-[#0a0a0a] border border-[#1f1f1f] ${compact ? "p-4" : "p-5"}`} data-testid={testid}>
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <div className="font-display font-bold text-base">{title}</div>
        {window ? <div className="text-[10px] uppercase tracking-wider text-white/40" data-testid={`${testid}-window`}>· {window}</div> : null}
      </div>
      {children}
    </div>
  );
}

function FunnelCell({ label, value, sub, highlight = false, testid }) {
  return (
    <div className={`rounded-lg p-3 border ${highlight ? "border-emerald-400/40 bg-emerald-500/5" : "border-white/10 bg-white/[0.02]"}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-white/50">{label}</div>
      <div className={`text-2xl font-black mt-1 ${highlight ? "text-emerald-300" : "text-white"}`}>{value ?? 0}</div>
      {sub ? <div className="text-[10px] text-white/40 mt-0.5">{sub}</div> : null}
    </div>
  );
}

function MoneyStat({ label, value, big = false, testid }) {
  return (
    <div className="flex items-baseline justify-between py-1" data-testid={testid}>
      <span className="text-xs text-white/50">{label}</span>
      <span className={`font-black text-emerald-300 ${big ? "text-2xl" : "text-lg"}`}>{value}</span>
    </div>
  );
}

function StatMini({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between py-1" data-testid={testid}>
      <span className="text-xs text-white/50">{label}</span>
      <span className="font-bold text-white text-sm">{value}</span>
    </div>
  );
}

function CreditSalesCard({ sales, winLabel, token, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const reconcile = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/billing/reconcile`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setMsg(`Checked ${data.checked} unfinished checkout${data.checked === 1 ? "" : "s"} · recovered ${data.fixed}${data.errors?.length ? ` · ${data.errors.length} lookup error(s)` : ""}`);
      onRefresh();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Stripe check failed.");
    } finally { setBusy(false); }
  };
  return (
    <Card title="Recent credit pack sales" window={winLabel} testid="credit-sales-card">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <button onClick={reconcile} disabled={busy} className="px-3 py-1.5 rounded-md text-xs font-bold bg-emerald-500 text-black disabled:opacity-50" data-testid="reconcile-credit-sales-btn">
          {busy ? "Checking Stripe…" : "Check Stripe for missed payments"}
        </button>
        {msg && <span className="text-xs text-white/60" data-testid="reconcile-result">{msg}</span>}
      </div>
      {sales.length === 0 ? (
        <div className="text-white/40 text-sm" data-testid="credit-sales-empty">No credit pack sales in this window.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-white/50 text-xs uppercase tracking-wider">
                <th className="pb-2">When</th>
                <th className="pb-2">Pack</th>
                <th className="pb-2">Buyer</th>
                <th className="pb-2 text-right">Credits</th>
                <th className="pb-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {sales.map((t) => (
                <tr key={t.session_id} className="border-t border-white/5" data-testid={`credit-sale-${t.session_id}`}>
                  <td className="py-2 text-white/50 text-xs">{t.paid_at ? new Date(t.paid_at).toLocaleString() : ""}</td>
                  <td className="py-2 font-bold">{t.pack_label}</td>
                  <td className="py-2 text-white/70">{t.buyer_username ? `${t.buyer_username} · ` : ""}{t.buyer_email}</td>
                  <td className="py-2 text-right text-white/70">{t.credits}</td>
                  <td className="py-2 text-right text-emerald-300 font-bold">{usd(t.amount_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
